/**
 * Ninja Scanner Service
 *
 * Service for managing Ninja-Agent-X security scanners.
 * Handles scanner registration, validation, and monitoring.
 *
 * Firebase document location /companies/{companyId}/ninjaScanner/{scannerId}
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, switchMap, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiConfigurationService } from './api-configuration.service';
import { LoggingService } from './logging.service';
import { environment } from 'src/environments/environment';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  deleteDoc,
  CollectionReference,
  DocumentData
} from '@angular/fire/firestore';

export type NinjaScannerPlatform = 'docker' | 'kubernetes' | 'binary' | 'aws-ecs' | 'azure-aci';

export interface NinjaScannerMetadata {
  version?: string;
  platform?: NinjaScannerPlatform;
  azureSubscriptionId?: string;
  azureTenantId?: string;
  azureResourceGroup?: string;
  azureLocation?: string;
}

export interface NinjaScannerRegistration {
  companyId: string;
  scannerName: string;
  description?: string;
}

export interface NinjaScannerResponse {
  success: boolean;
  scannerId: string;
  linkingKey: string;
  scannerName: string;
  status: string;
  message: string;
  activationCommand?: string;
  createdAt?: string;
}

export interface NinjaScanner {
  scannerId: string;
  linkingKey: string;
  companyId: string;
  scannerName: string;
  description: string;
  scannerType: string;
  status: 'pending' | 'active' | 'inactive';
  createdAt: any;
  activatedAt?: any;
  lastSeen?: any;
  metadata?: NinjaScannerMetadata;
}

export interface AcrCredentials {
  loginUsername?: string;
  loginPassword?: string;
  containerImage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NinjaScannerService {
  private http = inject(HttpClient);
  private apiConfigService = inject(ApiConfigurationService);
  private firestore = inject(Firestore);
  private loggingService = inject(LoggingService);

  private functionToken: string | null = null;
  private readonly COLLECTION_NAME = 'ninjaScanner';

  /**
   * Register a new Ninja Scanner
   */
  registerScanner(registration: NinjaScannerRegistration): Observable<NinjaScannerResponse> {
    return from(this.getApiUrlAndToken()).pipe(
      switchMap(({ url, token }) => {
        const endpoint = `${url}/api/ninja-scanner/register`;
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        if (token) {
          headers = headers.set('x-functions-key', token);
        }
        return this.http.post<any>(endpoint, registration, { headers });
      }),
      map((raw) => {
        // API may return flat or nested under a `data` key
        const d = raw?.data ?? raw;
        return {
          success: raw?.success ?? raw?.ok ?? true,
          scannerId: d?.scannerId || d?.scanner_id || d?.id || '',
          linkingKey: d?.linkingKey || d?.linking_key || d?.token || d?.auth_token || '',
          scannerName: d?.scannerName || d?.scanner_name || d?.name || registration.scannerName,
          status: d?.status || raw?.status || 'pending',
          message: raw?.message || '',
          activationCommand: d?.activationCommand || d?.activation_command,
          createdAt: d?.createdAt || raw?.createdAt,
        } as NinjaScannerResponse;
      })
    );
  }

  /**
   * Get scanner by ID from Firestore
   */
  getScanner(scannerId: string, companyId: string): Observable<NinjaScanner | null> {
    return from(this.getScannerFromFirestore(scannerId, companyId));
  }

  /**
   * Get scanner from Firestore (private helper)
   * Note: Requires companyId to be passed separately since scanners are now nested
   */
  private async getScannerFromFirestore(scannerId: string, companyId?: string): Promise<NinjaScanner | null> {
    try {
      if (!companyId) {
        this.loggingService.logError('Company ID required to fetch scanner from nested collection', {});
        return null;
      }
      const scannerDocRef = doc(this.firestore, 'companies', companyId, this.COLLECTION_NAME, scannerId);
      const scannerDoc = await getDoc(scannerDocRef);

      if (scannerDoc.exists()) {
        return { scannerId: scannerDoc.id, ...scannerDoc.data() } as NinjaScanner;
      }
      return null;
    } catch (error) {
      this.loggingService.logError('Error fetching scanner from Firestore', error);
      throw error;
    }
  }

  /**
   * List all scanners for a company
   */
  listScanners(companyId: string): Observable<NinjaScanner[]> {
    return from(this.getScannersFromFirestore(companyId));
  }

  /**
   * Get scanners from Firestore (private helper)
   */
  private async getScannersFromFirestore(companyId: string): Promise<NinjaScanner[]> {
    try {
      const scannersCollection = collection(this.firestore, 'companies', companyId, this.COLLECTION_NAME) as CollectionReference<DocumentData>;
      const q = query(scannersCollection, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);

      const scanners: NinjaScanner[] = [];
      querySnapshot.forEach((doc) => {
        scanners.push({ scannerId: doc.id, ...doc.data() } as NinjaScanner);
      });

      return scanners;
    } catch (error) {
      this.loggingService.logError('Error fetching scanners from Firestore', error);
      throw error;
    }
  }

  /**
   * Delete a scanner
   */
  deleteScanner(scannerId: string, companyId: string): Observable<void> {
    return from(this.deleteScannerFromFirestore(scannerId, companyId));
  }

  /**
   * Re-provision the control-plane auth token for a scanner whose token was
   * not written during initial registration (e.g. transient DB failure).
   * The running container does NOT need to be restarted — it already carries
   * the correct linking key; this call simply ensures the control plane
   * recognises that key again.
   */
  reprovisionScanner(scannerId: string, companyId: string): Observable<{ success: boolean; message: string }> {
    return from(this.getApiUrlAndToken()).pipe(
      switchMap(({ url, token }) => {
        const endpoint = `${url}/api/ninja-scanner/${encodeURIComponent(scannerId)}/reprovision?companyId=${encodeURIComponent(companyId)}`;
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        if (token) {
          headers = headers.set('x-functions-key', token);
        }
        return this.http.post<{ success: boolean; message: string }>(endpoint, {}, { headers });
      }),
      catchError(error => {
        this.loggingService.logError('Error re-provisioning scanner', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * List scans that have been executed by or assigned to a specific scanner.
   * Proxies to the Ninja-Agent-X control plane GET /api/v1/scans, scoped to
   * the company tenant.
   */
  listScannerScans(scannerId: string, companyId: string): Observable<any[]> {
    return from(this.getApiUrlAndToken()).pipe(
      switchMap(({ url, token }) => {
        const endpoint = `${url}/api/ninja-scanner/${encodeURIComponent(scannerId)}/list-scans?companyId=${encodeURIComponent(companyId)}`;
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        if (token) {
          headers = headers.set('x-functions-key', token);
        }
        return this.http.get<any>(endpoint, { headers });
      }),
      map((raw: any) => raw?.scans ?? raw?.data ?? (Array.isArray(raw) ? raw : [])),
      catchError(error => {
        this.loggingService.logError('Error listing scanner scans', error);
        return throwError(() => error);
      }),
    );
  }

  updateScannerDeploymentMetadata(
    scannerId: string,
    companyId: string,
    metadata: NinjaScannerMetadata
  ): Observable<void> {
    return from(this.updateScannerDeploymentMetadataInFirestore(scannerId, companyId, metadata));
  }

  /**
   * Update scanner profile fields (name, description, metadata). Whitelists
   * mutable fields so callers cannot overwrite identity / status columns.
   *
   * Partial `metadata` patches merge into the existing metadata sub-document
   * field-by-field (e.g. updating `platform` alone leaves `azureSubscriptionId`
   * intact). Callers do NOT need to send the full metadata object.
   */
  updateScanner(
    scannerId: string,
    companyId: string,
    patch: Partial<Pick<NinjaScanner, 'scannerName' | 'description'>> & { metadata?: Partial<NinjaScannerMetadata> },
  ): Observable<void> {
    return from(this.updateScannerInFirestore(scannerId, companyId, patch));
  }

  private async updateScannerInFirestore(
    scannerId: string,
    companyId: string,
    patch: Partial<Pick<NinjaScanner, 'scannerName' | 'description'>> & { metadata?: Partial<NinjaScannerMetadata> },
  ): Promise<void> {
    const scannerDocRef = doc(this.firestore, 'companies', companyId, this.COLLECTION_NAME, scannerId);
    const update: Record<string, any> = { updatedAt: new Date().toISOString() };
    if (patch.scannerName !== undefined) update['scannerName'] = patch.scannerName;
    if (patch.description !== undefined) update['description'] = patch.description;
    if (patch.metadata) {
      // Use dotted field paths so each metadata key is merged into the existing
      // sub-document instead of replacing the whole map. Without this, sending
      // `{ platform: 'k8s' }` would silently drop azureSubscriptionId etc.
      for (const [k, v] of Object.entries(patch.metadata)) {
        if (v === undefined || v === null || v === '') continue;
        update[`metadata.${k}`] = v;
      }
    }
    await updateDoc(scannerDocRef, update);
  }

  /**
   * Delete scanner from Firestore (private helper)
   */
  private async deleteScannerFromFirestore(scannerId: string, companyId: string): Promise<void> {
    try {
      const scannerDocRef = doc(this.firestore, 'companies', companyId, this.COLLECTION_NAME, scannerId);
      await deleteDoc(scannerDocRef);
    } catch (error) {
      this.loggingService.logError('Error deleting scanner from Firestore', error);
      throw error;
    }
  }

  private async updateScannerDeploymentMetadataInFirestore(
    scannerId: string,
    companyId: string,
    metadata: NinjaScannerMetadata
  ): Promise<void> {
    const scannerDocRef = doc(this.firestore, 'companies', companyId, this.COLLECTION_NAME, scannerId);

    const cleanedMetadata = Object.fromEntries(
      Object.entries(metadata).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );

    await updateDoc(scannerDocRef, {
      metadata: cleanedMetadata,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Get scanner status (active, inactive, etc.)
   */
  getScannerStatus(scannerId: string, companyId: string): Observable<{ status: string; lastSeen?: Date }> {
    return this.getScanner(scannerId, companyId).pipe(
      map(scanner => {
        if (!scanner) {
          throw new Error('Scanner not found');
        }

        let lastSeen: Date | undefined;
        if (scanner.lastSeen) {
          lastSeen = scanner.lastSeen.toDate ? scanner.lastSeen.toDate() : new Date(scanner.lastSeen);
        }

        return {
          status: scanner.status,
          lastSeen
        };
      }),
      catchError(error => {
        this.loggingService.logError('Error getting scanner status', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Generate deployment script for the scanner
   */
  generateDeploymentScript(
    scanner: NinjaScanner,
    platform: NinjaScannerPlatform,
    acrCredentials?: AcrCredentials
  ): string {
    const linkingKey = scanner.linkingKey || '<linking-key>';
    const controlPlaneUrl = environment.ninja_agent_grpc_url || environment.ninja_agent_api_url;
    const agentId = scanner.scannerId || '<scanner-id>';
    const agentName = scanner.scannerName || 'ninja-agent';
    const shortId = agentId.substring(0, 8);
    const metadata = scanner.metadata ?? {};
    const azureSubscriptionId = metadata.azureSubscriptionId?.trim() || '';
    const azureTenantId = metadata.azureTenantId?.trim() || '';
    const azureResourceGroup = metadata.azureResourceGroup?.trim() || 'Rg-ScanNinja-Agents';
    const azureLocation = metadata.azureLocation?.trim() || 'eastus';

    // Use ACR image if credentials provided, otherwise use public image
    const dockerImage = acrCredentials?.containerImage || environment.ninja_agent_image;
    const hasAcrCreds = !!(acrCredentials?.loginUsername && acrCredentials?.loginPassword);
    const acrServer = dockerImage.split('/')[0];

    switch (platform) {
      case 'docker':
        if (hasAcrCreds) {
          return `# ╔════════════════════════════════════════════════════════════════════════════╗
# ║                  Ninja-Agent-X Scanner Agent Deployment                   ║
# ╚════════════════════════════════════════════════════════════════════════════╝
#
# This script deploys the Ninja-Agent-X Scanner Agent (ninja-agent-agent)
# which connects to the Scan Ninja control plane to receive and execute scans.
#
#  📦 Image: ${dockerImage}
#     (pinned release; pull it with the registry token shown with this script)
#
# ─── Prerequisites ─────────────────────────────────────────────────────────────
#  • Docker installed and running
#  • Network access to control plane: ${controlPlaneUrl}
#  • Valid linking key (provided below)
#
# ═══════════════════════════════════════════════════════════════════════════════

# Control plane URL: ${controlPlaneUrl}
# Scanner ID:        ${agentId}
# Linking key:       ${linkingKey}

# Step 1: Login to Azure Container Registry
echo "Authenticating with Azure Container Registry..."
docker login ${acrServer} -u ${acrCredentials.loginUsername} -p ${acrCredentials.loginPassword}

# Step 2: Pull the scanner agent image
echo "Pulling ninja-agent-agent image..."
docker pull ${dockerImage}

# Step 3: Run the scanner agent
echo "Starting scanner agent..."
docker run -d \\
  --name ninja-agent-${shortId} \\
  -e NINJA_AGENT__AGENT__ID="${agentId}" \\
  -e NINJA_AGENT__AGENT__NAME="${agentName}" \\
  -e NINJA_AGENT__CONTROL_PLANE__URL="${controlPlaneUrl}" \\
  -e NINJA_AGENT__CONTROL_PLANE__AUTH_TOKEN="${linkingKey}" \\
  --restart unless-stopped \\
  ${dockerImage}

echo ""
echo "✅ Scanner agent deployed successfully!"
echo ""
echo "View logs:     docker logs -f ninja-agent-${shortId}"
echo "Stop agent:    docker stop ninja-agent-${shortId}"
echo "Remove agent:  docker rm -f ninja-agent-${shortId}"
echo ""
`;
        } else {
          return `# ╔════════════════════════════════════════════════════════════════════════════╗
# ║                  Ninja-Agent-X Scanner Agent Deployment                   ║
# ╚════════════════════════════════════════════════════════════════════════════╝
#
# Control plane: ${controlPlaneUrl}
# Auth token:    ${linkingKey}

docker pull ${dockerImage}

docker run -d \\
  --name ninja-agent-${shortId} \\
  -e NINJA_AGENT__AGENT__ID="${agentId}" \\
  -e NINJA_AGENT__AGENT__NAME="${agentName}" \\
  -e NINJA_AGENT__CONTROL_PLANE__URL="${controlPlaneUrl}" \\
  -e NINJA_AGENT__CONTROL_PLANE__AUTH_TOKEN="${linkingKey}" \\
  --restart unless-stopped \\
  ${dockerImage}

echo ""
echo "✅ Scanner agent deployed!"
echo "View logs: docker logs -f ninja-agent-${shortId}"
`;
        }

      case 'kubernetes': {
        const secretSection = hasAcrCreds ? `
# Step 2: Create Docker registry secret for ACR
echo "Creating ACR secret..."
kubectl get secret ninja-acr-secret --namespace="$NAMESPACE" 2>/dev/null || \\
kubectl create secret docker-registry ninja-acr-secret \\
  --namespace="$NAMESPACE" \\
  --docker-server=${acrServer} \\
  --docker-username="${acrCredentials.loginUsername}" \\
  --docker-password="${acrCredentials.loginPassword}"

# Step 3: Create secret for agent auth token` : `
# Step 2: Create secret for the agent auth token`;

        const imagePullSecretsSection = hasAcrCreds ? `      imagePullSecrets:
      - name: ninja-acr-secret
      containers:` : `      containers:`;

        return `#!/bin/bash
# Deploy Ninja-Agent-X to Kubernetes
# Prerequisites: kubectl configured to access your cluster

set -e  # Exit on any error

NAMESPACE="ninja-agents"
SECRET_NAME="ninja-agent-secret-${shortId}"
DEPLOYMENT_NAME="ninja-agent-${shortId}"

echo "Setting up Kubernetes resources..."

# Step 1: Create namespace if it doesn't exist
echo "Ensuring namespace exists..."
kubectl get namespace "$NAMESPACE" 2>/dev/null || kubectl create namespace "$NAMESPACE"
${secretSection}
echo "Creating agent secret..."
kubectl get secret "$SECRET_NAME" --namespace="$NAMESPACE" 2>/dev/null || \\
kubectl create secret generic "$SECRET_NAME" \\
  --namespace="$NAMESPACE" \\
  --from-literal=CONTROL_PLANE_AUTH_TOKEN="${linkingKey}"

# Step ${hasAcrCreds ? '4' : '3'}: Apply the deployment
echo "Deploying agent..."
cat <<EOF | kubectl apply -f -
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: $DEPLOYMENT_NAME
  namespace: $NAMESPACE
  labels:
    app: ninja-agent
    agent-id: ${shortId}
spec:
  replicas: 1
  selector:
    matchLabels:
      app: ninja-agent
      agent-id: ${shortId}
  template:
    metadata:
      labels:
        app: ninja-agent
        agent-id: ${shortId}
    spec:
${imagePullSecretsSection}
      - name: ninja-agent
        image: ${dockerImage}
        env:
        - name: NINJA_AGENT__AGENT__ID
          value: "${agentId}"
        - name: NINJA_AGENT__AGENT__NAME
          value: "${agentName}"
        - name: NINJA_AGENT__CONTROL_PLANE__URL
          value: "${controlPlaneUrl}"
        - name: NINJA_AGENT__CONTROL_PLANE__AUTH_TOKEN
          valueFrom:
            secretKeyRef:
              name: $SECRET_NAME
              key: CONTROL_PLANE_AUTH_TOKEN
        resources:
          limits:
            memory: "512Mi"
            cpu: "500m"
          requests:
            memory: "256Mi"
            cpu: "250m"
        imagePullPolicy: Always
      restartPolicy: Always
EOF

echo ""
echo "Deployment complete!"
echo ""
echo "Check deployment status:"
echo "  kubectl get deployment $DEPLOYMENT_NAME --namespace=$NAMESPACE"
echo ""
echo "View pod logs:"
echo "  kubectl logs -l agent-id=${shortId} --namespace=$NAMESPACE -f"
echo ""
echo "Check pod status:"
echo "  kubectl get pods -l agent-id=${shortId} --namespace=$NAMESPACE"
echo ""
echo "Delete deployment:"
echo "  kubectl delete deployment $DEPLOYMENT_NAME --namespace=$NAMESPACE"
echo "  kubectl delete secret $SECRET_NAME --namespace=$NAMESPACE"${hasAcrCreds ? `
echo "  kubectl delete secret ninja-acr-secret --namespace=$NAMESPACE"` : ''}
`;
      }

      case 'binary': {
        // The installer downloads the latest signed binary from the
        // Ninja-Agent-X GitHub Release (.github/workflows/release.yml).
        // Asset naming pinned by the release workflow:
        //   ninja-agent-agent-<os>-<arch>.{tar.gz|zip}
        //   checksums.txt
        // The script:
        //   1. Detects OS + arch via `uname`
        //   2. Downloads the matching archive from /releases/latest/download/
        //   3. Verifies SHA-256 against the published checksums.txt
        //   4. Extracts to /usr/local/bin (or $NINJA_AGENT_INSTALL_DIR)
        //   5. Writes a systemd unit so it survives reboots
        //   6. Starts the agent
        //
        // If no release has been published yet, the curl 404s with a clear
        // error rather than silently producing a broken install.
        const releaseRepo = 'ScanNinja/Ninja-Agent-X';
        const releaseBase = `https://github.com/${releaseRepo}/releases/latest/download`;
        return `#!/usr/bin/env bash
# ╔════════════════════════════════════════════════════════════════════════════╗
# ║       Ninja-Agent-X Scanner Agent — Binary Installer (Linux/macOS)        ║
# ╚════════════════════════════════════════════════════════════════════════════╝
#
# Downloads the latest signed release of ninja-agent-agent from GitHub
# Releases, SHA-256-verifies it, installs to /usr/local/bin, and
# registers a systemd unit so it survives reboots.
#
# For Windows, see the PowerShell snippet at the bottom of this script.
#
# Control plane:  ${controlPlaneUrl}
# Scanner ID:     ${agentId}
# Linking key:    ${linkingKey}
#
# Pin a specific version by exporting NINJA_AGENT_VERSION (e.g. v0.1.0).
# Default: latest published release.

set -euo pipefail

INSTALL_DIR="${'${'}NINJA_AGENT_INSTALL_DIR:-/usr/local/bin}"
RELEASE_BASE="${'${'}NINJA_AGENT_RELEASE_BASE:-${releaseBase}}"
VERSION="${'${'}NINJA_AGENT_VERSION:-}"

# ── Detect OS + arch ────────────────────────────────────────────────────────
case "$(uname -s)" in
  Linux)  OS=linux  ;;
  Darwin) OS=macos  ;;
  *) echo "Unsupported OS: $(uname -s). See PowerShell snippet for Windows." >&2; exit 1 ;;
esac

case "$(uname -m)" in
  x86_64|amd64)         ARCH=amd64 ;;
  aarch64|arm64)        ARCH=arm64 ;;
  *) echo "Unsupported architecture: $(uname -m)." >&2; exit 1 ;;
esac

ASSET="ninja-agent-agent-${'${'}OS}-${'${'}ARCH}.tar.gz"
CHECKSUMS="checksums.txt"

# Allow VERSION pinning (defaults to /latest/download which 302s to the
# newest release).
if [[ -n "$VERSION" ]]; then
  RELEASE_BASE="https://github.com/${releaseRepo}/releases/download/$VERSION"
fi

echo "Installing ninja-agent-agent for ${'${'}OS}/${'${'}ARCH}…"
echo "  Source: ${'${'}RELEASE_BASE}/${'${'}ASSET}"
echo "  Target: ${'${'}INSTALL_DIR}"

# ── Download ────────────────────────────────────────────────────────────────
TMP="$(mktemp -d)"
trap "rm -rf '$TMP'" EXIT

# --fail makes curl exit non-zero on 4xx/5xx so a missing release fails loud
# instead of writing an HTML 404 page to disk.
if ! curl -fsSL "${'${'}RELEASE_BASE}/${'${'}ASSET}" -o "$TMP/${'${'}ASSET}"; then
  cat >&2 <<'EOMSG'
ERROR: Release asset not found.

This usually means no GitHub Release has been published yet (the project
ships binaries via the .github/workflows/release.yml workflow, which is
triggered on tag push). Until the first release is cut, please use the
Docker, Kubernetes, or Azure ACI deployment options instead.
EOMSG
  exit 1
fi

# ── SHA-256 verify (PCI 6.5 / SOC 2 CC8.1 supply-chain hygiene) ────────────
curl -fsSL "${'${'}RELEASE_BASE}/${'${'}CHECKSUMS}" -o "$TMP/${'${'}CHECKSUMS}"
(cd "$TMP" && grep "  ${'${'}ASSET}$" "${'${'}CHECKSUMS}" | sha256sum --check --strict)

# ── Extract + install ──────────────────────────────────────────────────────
tar -xzf "$TMP/${'${'}ASSET}" -C "$TMP"
if [[ -w "$INSTALL_DIR" ]]; then
  install -m 0755 "$TMP/ninja-agent-agent" "$INSTALL_DIR/ninja-agent-agent"
else
  echo "  (sudo required to write to $INSTALL_DIR)"
  sudo install -m 0755 "$TMP/ninja-agent-agent" "$INSTALL_DIR/ninja-agent-agent"
fi

echo "Installed: $($INSTALL_DIR/ninja-agent-agent --version 2>/dev/null || echo "$INSTALL_DIR/ninja-agent-agent")"

# ── Persist env vars in /etc/ninja-agent.env (used by the systemd unit) ───
ENV_FILE="/etc/ninja-agent.env"
write_env() {
  cat <<EOF
NINJA_AGENT__AGENT__ID=${agentId}
NINJA_AGENT__AGENT__NAME=${agentName}
NINJA_AGENT__CONTROL_PLANE__URL=${controlPlaneUrl}
NINJA_AGENT__CONTROL_PLANE__AUTH_TOKEN=${linkingKey}
EOF
}
if [[ -w "$(dirname "$ENV_FILE")" ]]; then
  write_env > "$ENV_FILE"
  chmod 0600 "$ENV_FILE"
else
  write_env | sudo tee "$ENV_FILE" >/dev/null
  sudo chmod 0600 "$ENV_FILE"
fi

# ── Install systemd unit on Linux (skip on macOS — use launchd manually) ──
if [[ "$OS" == "linux" ]] && command -v systemctl >/dev/null 2>&1; then
  echo "Registering systemd service…"
  UNIT_FILE="/etc/systemd/system/ninja-agent-${shortId}.service"
  cat <<EOF | sudo tee "$UNIT_FILE" >/dev/null
[Unit]
Description=Ninja-Agent-X Scanner Agent (${shortId})
After=network-online.target
Wants=network-online.target

[Service]
Type=simple
EnvironmentFile=$ENV_FILE
ExecStart=$INSTALL_DIR/ninja-agent-agent
Restart=on-failure
RestartSec=5
# Least-privilege defaults (PCI 7 / SOC 2 CC6.1)
DynamicUser=true
NoNewPrivileges=true
ProtectSystem=strict
ProtectHome=true
PrivateTmp=true
PrivateDevices=true
ProtectKernelTunables=true
ProtectKernelModules=true
ProtectControlGroups=true
RestrictAddressFamilies=AF_INET AF_INET6
LockPersonality=true

[Install]
WantedBy=multi-user.target
EOF
  sudo systemctl daemon-reload
  sudo systemctl enable --now "ninja-agent-${shortId}.service"
  echo ""
  echo "Status:  sudo systemctl status ninja-agent-${shortId}.service"
  echo "Logs:    journalctl -u ninja-agent-${shortId}.service -f"
  echo "Stop:    sudo systemctl stop ninja-agent-${shortId}.service"
  echo "Remove:  sudo systemctl disable --now ninja-agent-${shortId}.service && sudo rm $UNIT_FILE"
else
  echo ""
  echo "Skipping systemd registration (not Linux, or systemd not available)."
  echo "Run manually:"
  echo "  set -a; source $ENV_FILE; set +a; $INSTALL_DIR/ninja-agent-agent"
fi

echo ""
echo "✅ Scanner agent installed and running."

# ─────────────────────────────────────────────────────────────────────────────
# Windows alternative (PowerShell — run as Administrator):
# -----------------------------------------------------------------------------
# $ErrorActionPreference = 'Stop'
# $base   = '${releaseBase}'
# $asset  = 'ninja-agent-agent-windows-amd64.zip'
# $dest   = "$env:ProgramFiles\\NinjaAgent"
# New-Item -ItemType Directory -Force $dest | Out-Null
# Invoke-WebRequest "$base/$asset" -OutFile "$env:TEMP\\$asset"
# Invoke-WebRequest "$base/checksums.txt" -OutFile "$env:TEMP\\checksums.txt"
# $expected = (Select-String -Path "$env:TEMP\\checksums.txt" -Pattern $asset).Line.Split()[0]
# $actual   = (Get-FileHash "$env:TEMP\\$asset" -Algorithm SHA256).Hash.ToLower()
# if ($expected -ne $actual) { throw 'Checksum mismatch — aborting install' }
# Expand-Archive "$env:TEMP\\$asset" -DestinationPath $dest -Force
# [Environment]::SetEnvironmentVariable('NINJA_AGENT__AGENT__ID',                 '${agentId}',         'Machine')
# [Environment]::SetEnvironmentVariable('NINJA_AGENT__AGENT__NAME',               '${agentName}',       'Machine')
# [Environment]::SetEnvironmentVariable('NINJA_AGENT__CONTROL_PLANE__URL',        '${controlPlaneUrl}', 'Machine')
# [Environment]::SetEnvironmentVariable('NINJA_AGENT__CONTROL_PLANE__AUTH_TOKEN', '${linkingKey}',      'Machine')
# # Register as a Windows Service (requires nssm or sc.exe):
# sc.exe create "NinjaAgent-${shortId}" binPath= "$dest\\ninja-agent-agent.exe" start= auto
# sc.exe start  "NinjaAgent-${shortId}"
`;
      }

      case 'aws-ecs': {
        // Two changes from the previous version of this script:
        //   1. ACR creds → AWS Secrets Manager (the documented secure
        //      pattern). The task definition references the secret via
        //      `repositoryCredentials.credentialsParameter` so the password
        //      never lives in shell history, task-definition JSON, or
        //      CloudTrail. PCI 3.5 / SOC 2 CC6.7.
        //   2. The EC2 user-data fallback is gone. It embedded the ACR
        //      password in plaintext user-data — readable by anyone with
        //      ec2:DescribeInstanceAttribute. Removed; use Docker locally
        //      or Azure ACI for an interactive single-host deploy.
        //   3. Auto-discovers the default VPC's first subnet + security
        //      group so the final `aws ecs run-task` doesn't require the
        //      operator to fill in placeholders by hand.
        const acrSecretsBlock = hasAcrCreds ? `
# ── Step 3a: Store ACR credentials in AWS Secrets Manager ──────────────────
# Pattern: { "username": "...", "password": "..." } — required by ECS for
# repositoryCredentials. Never echo the password to stdout (set +x ensures
# it doesn't end up in shell -x traces either).
echo "Storing ACR credentials in AWS Secrets Manager..."
set +x
SECRET_NAME="ninja-agent-acr/${shortId}"
SECRET_PAYLOAD=$(cat <<JSON
{"username":"${acrCredentials.loginUsername}","password":"${acrCredentials.loginPassword}"}
JSON
)
ACR_SECRET_ARN=$(aws secretsmanager create-secret \\
  --name "$SECRET_NAME" \\
  --region "$REGION" \\
  --secret-string "$SECRET_PAYLOAD" \\
  --query 'ARN' --output text 2>/dev/null || \\
  aws secretsmanager put-secret-value \\
    --secret-id "$SECRET_NAME" \\
    --region "$REGION" \\
    --secret-string "$SECRET_PAYLOAD" \\
    --query 'ARN' --output text)
unset SECRET_PAYLOAD

# Step 3b: Grant the task-execution role permission to read this secret.
aws iam put-role-policy \\
  --role-name "$TASK_ROLE_NAME" \\
  --policy-name "NinjaAgentReadAcrSecret-${shortId}" \\
  --policy-document "$(cat <<EOF
{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": ["secretsmanager:GetSecretValue"],
    "Resource": "$ACR_SECRET_ARN"
  }]
}
EOF
)"
` : '';

        const repositoryCredentialsField = hasAcrCreds ? `
    "repositoryCredentials": {
      "credentialsParameter": "'"$ACR_SECRET_ARN"'"
    },` : '';

        return `#!/bin/bash
# ╔════════════════════════════════════════════════════════════════════════════╗
# ║      Ninja-Agent-X Scanner Agent — AWS ECS Fargate Deployment             ║
# ╚════════════════════════════════════════════════════════════════════════════╝
#
# Prerequisites:
#   - AWS CLI v2 installed and configured (\`aws configure\`)
#   - An AWS account with permission to create ECS / IAM / Logs / Secrets
#   - Default VPC present in the chosen region (used for auto-discovery)
#
# Auth tokens in this script:
#   - Control-plane linking key:  passed via task-definition environment
#   - ACR registry password:      stored in AWS Secrets Manager${hasAcrCreds ? ' (this script)' : ' (not used — public image)'}

set -euo pipefail

CLUSTER_NAME="ninja-agent-cluster"
REGION="\${AWS_REGION:-us-east-1}"
LOG_GROUP="/ecs/ninja-agent"
TASK_ROLE_NAME="ecsTaskExecutionRole-NinjaAgent"

echo "Setting up AWS ECS infrastructure in region $REGION..."

# Step 1: Create CloudWatch Logs group if it doesn't exist
echo "Creating CloudWatch log group..."
aws logs create-log-group \\
  --log-group-name "$LOG_GROUP" \\
  --region "$REGION" 2>/dev/null || echo "  Log group already exists"

# Step 2: Create ECS cluster if it doesn't exist
echo "Creating ECS cluster..."
aws ecs create-cluster \\
  --cluster-name "$CLUSTER_NAME" \\
  --region "$REGION" >/dev/null 2>&1 || echo "  Cluster already exists"

# Step 3: Task-execution role (logs + image pull + secret read)
echo "Ensuring IAM task execution role $TASK_ROLE_NAME exists..."
if ! aws iam get-role --role-name "$TASK_ROLE_NAME" >/dev/null 2>&1; then
  TRUST_DOC='{"Version":"2012-10-17","Statement":[{"Effect":"Allow","Principal":{"Service":"ecs-tasks.amazonaws.com"},"Action":"sts:AssumeRole"}]}'
  aws iam create-role \\
    --role-name "$TASK_ROLE_NAME" \\
    --assume-role-policy-document "$TRUST_DOC" >/dev/null
  aws iam attach-role-policy \\
    --role-name "$TASK_ROLE_NAME" \\
    --policy-arn arn:aws:iam::aws:policy/service-role/AmazonECSTaskExecutionRolePolicy
  echo "  Waiting 15s for IAM role propagation..."
  sleep 15
fi
ROLE_ARN=$(aws iam get-role --role-name "$TASK_ROLE_NAME" --query 'Role.Arn' --output text)
${acrSecretsBlock}
# ── Step 4: Register the task definition ────────────────────────────────────
echo "Registering task definition..."
aws ecs register-task-definition --region "$REGION" --cli-input-json '{
  "family": "ninja-agent-${shortId}",
  "networkMode": "awsvpc",
  "requiresCompatibilities": ["FARGATE"],
  "cpu": "256",
  "memory": "512",
  "executionRoleArn": "'"$ROLE_ARN"'",
  "containerDefinitions": [{
    "name": "ninja-agent",
    "image": "${dockerImage}",
    "essential": true,${repositoryCredentialsField}
    "environment": [
      {"name": "NINJA_AGENT__AGENT__ID",              "value": "${agentId}"},
      {"name": "NINJA_AGENT__AGENT__NAME",            "value": "${agentName}"},
      {"name": "NINJA_AGENT__CONTROL_PLANE__URL",     "value": "${controlPlaneUrl}"},
      {"name": "NINJA_AGENT__CONTROL_PLANE__AUTH_TOKEN", "value": "${linkingKey}"}
    ],
    "logConfiguration": {
      "logDriver": "awslogs",
      "options": {
        "awslogs-group": "'"$LOG_GROUP"'",
        "awslogs-region": "'"$REGION"'",
        "awslogs-stream-prefix": "ecs"
      }
    }
  }]
}' >/dev/null

# ── Step 5: Auto-discover the default VPC's first subnet + security group ──
# Operators with a non-default VPC should export SUBNET_ID and SG_ID before
# running this script to skip the auto-discovery.
SUBNET_ID="\${SUBNET_ID:-}"
SG_ID="\${SG_ID:-}"
if [[ -z "$SUBNET_ID" ]]; then
  SUBNET_ID=$(aws ec2 describe-subnets --region "$REGION" \\
    --filters Name=default-for-az,Values=true \\
    --query 'Subnets[0].SubnetId' --output text)
fi
if [[ -z "$SG_ID" ]]; then
  SG_ID=$(aws ec2 describe-security-groups --region "$REGION" \\
    --filters Name=group-name,Values=default \\
    --query 'SecurityGroups[0].GroupId' --output text)
fi
if [[ "$SUBNET_ID" == "None" || "$SG_ID" == "None" ]]; then
  echo "ERROR: Could not auto-discover default VPC subnet/SG."
  echo "       Export SUBNET_ID and SG_ID and re-run." >&2
  exit 1
fi

# ── Step 6: Launch the task ────────────────────────────────────────────────
echo "Launching task on subnet $SUBNET_ID, security group $SG_ID..."
TASK_ARN=$(aws ecs run-task \\
  --cluster "$CLUSTER_NAME" \\
  --task-definition "ninja-agent-${shortId}" \\
  --launch-type FARGATE \\
  --region "$REGION" \\
  --network-configuration "awsvpcConfiguration={subnets=[$SUBNET_ID],securityGroups=[$SG_ID],assignPublicIp=ENABLED}" \\
  --query 'tasks[0].taskArn' --output text)

echo ""
echo "Scanner agent launched:"
echo "  Task ARN:  $TASK_ARN"
echo "  Cluster:   $CLUSTER_NAME (region $REGION)"
echo ""
echo "Useful follow-up commands:"
echo "  Status:    aws ecs describe-tasks --cluster $CLUSTER_NAME --tasks $TASK_ARN --region $REGION"
echo "  Logs:      aws logs tail $LOG_GROUP --follow --region $REGION"
echo "  Stop:      aws ecs stop-task --cluster $CLUSTER_NAME --task $TASK_ARN --region $REGION"
`;
      }

      case 'azure-aci': {
        const aciImageRegistry = hasAcrCreds ? `  --registry-login-server "${acrServer}" \\
  --registry-username "${acrCredentials.loginUsername}" \\
  --registry-password "${acrCredentials.loginPassword}" \\
` : '';

        return `#!/bin/bash
# Deploy Ninja-Agent-X to Azure Container Instances
# Prerequisites: Azure CLI installed (az login)

set -euo pipefail

# Configuration
RESOURCE_GROUP="${azureResourceGroup}"
LOCATION="${azureLocation}"
CONTAINER_NAME="ninja-agent-${shortId}"

AZURE_SUBSCRIPTION_ID="\${AZURE_SUBSCRIPTION_ID:-${azureSubscriptionId}}"
AZURE_TENANT_ID="\${AZURE_TENANT_ID:-${azureTenantId}}"

if [[ -z "$AZURE_SUBSCRIPTION_ID" ]]; then
  AZURE_SUBSCRIPTION_ID="$(az account show --query id --output tsv 2>/dev/null || true)"
fi

if [[ -z "$AZURE_TENANT_ID" ]]; then
  AZURE_TENANT_ID="$(az account show --query tenantId --output tsv 2>/dev/null || true)"
fi

if [[ -z "$AZURE_SUBSCRIPTION_ID" ]]; then
  echo "Azure subscription could not be determined. Run 'az login' or export AZURE_SUBSCRIPTION_ID first." >&2
  exit 1
fi

echo "Setting up Azure infrastructure..."
echo "Using Azure subscription: $AZURE_SUBSCRIPTION_ID"

# Step 1: Create resource group if it doesn't exist
echo "Ensuring resource group exists..."
az group create \\
  --name "$RESOURCE_GROUP" \\
  --location "$LOCATION" \\
  --output none 2>/dev/null || echo "Resource group already exists"

echo "Deploying container to Azure Container Instances..."
echo ""

# Step 2: Deploy container${hasAcrCreds ? ' with ACR authentication' : ''}
az container create \\
  --resource-group "$RESOURCE_GROUP" \\
  --name "$CONTAINER_NAME" \\
  --image "${dockerImage}" \\
${aciImageRegistry}  --os-type Linux \\
  --restart-policy Always \\
  --location "$LOCATION" \\
  --cpu 0.5 \\
  --memory 0.5 \\
  --environment-variables \\
    NINJA_AGENT__AGENT__ID="${agentId}" \\
    NINJA_AGENT__AGENT__NAME="${agentName}" \\
    NINJA_AGENT__CONTROL_PLANE__URL="${controlPlaneUrl}" \\
    NINJA_AGENT__CONTROL_PLANE__AUTH_TOKEN="${linkingKey}" \\
    NINJA_AGENT__CLOUD__PROVIDER="azure" \\
    NINJA_AGENT__CLOUD__AZURE__SUBSCRIPTION_ID="$AZURE_SUBSCRIPTION_ID" \\
    AZURE_SUBSCRIPTION_ID="$AZURE_SUBSCRIPTION_ID" \\
    AZURE_TENANT_ID="$AZURE_TENANT_ID" \\
    NINJA_AGENT__CLOUD__AZURE__TENANT_ID="$AZURE_TENANT_ID"

echo ""
echo "Deployment initiated. Checking status..."
echo ""

# Step 3: Check status
az container show \\
  --resource-group "$RESOURCE_GROUP" \\
  --name "$CONTAINER_NAME" \\
  --query "{provisioningState:provisioningState, containerState:containers[0].instanceView.currentState.state, detailStatus:containers[0].instanceView.currentState.detailStatus}" \\
  --output table

echo ""
echo "Fetching logs..."
echo ""

# Step 4: View logs
az container logs \\
  --resource-group "$RESOURCE_GROUP" \\
  --name "$CONTAINER_NAME"

echo ""
echo "Useful follow-up commands:"
echo "  Show state: az container show --resource-group '$RESOURCE_GROUP' --name '$CONTAINER_NAME' --query '{provisioningState:provisioningState,containerState:containers[0].instanceView.currentState.state,detailStatus:containers[0].instanceView.currentState.detailStatus}' --output table"
echo "  Fetch logs: az container logs --resource-group '$RESOURCE_GROUP' --name '$CONTAINER_NAME'"

# Useful management commands:
# Stop container:    az container stop --resource-group "$RESOURCE_GROUP" --name "$CONTAINER_NAME"
# Start container:   az container start --resource-group "$RESOURCE_GROUP" --name "$CONTAINER_NAME"
# Delete container:  az container delete --resource-group "$RESOURCE_GROUP" --name "$CONTAINER_NAME" --yes
# Show details:      az container show --resource-group "$RESOURCE_GROUP" --name "$CONTAINER_NAME"
`;
      }

      default:
        return '';
    }
  }

  /**
   * Resolve the API base URL and function token from runtime configuration
   */
  private async getApiUrlAndToken(): Promise<{ url: string; token: string | null }> {
    const [url, token] = await Promise.all([
      this.apiConfigService.getCveFunctionUrl().catch(() => 'https://dev.cve.scanninja.app'),
      this.ensureFunctionToken(),
    ]);
    return { url, token };
  }

  private async ensureFunctionToken(): Promise<string | null> {
    if (this.functionToken) return this.functionToken;
    try {
      const result = await this.apiConfigService.getFunctionToken();
      this.functionToken = result?.value || null;
    } catch {
      this.functionToken = null;
    }
    return this.functionToken;
  }
}
